import API_CONFIG from '../config/api.config';
import apiClient from './apiClient';
import { MENTORS } from '../data/mockData';
import { getCached, setCached } from '../utils/dashboardCache';

const delay = (ms = API_CONFIG.MOCK_DELAY) => new Promise(r => setTimeout(r, ms));

// ── Normalize API mentor shape → component shape ──────────────
// API returns: { userId: { avatar, avatarBg, ... }, currentRole, company, skills[], ... }
// Component expects: { name, role, company, priceINR, skills[], ... }

function nameFromSlug(slug) {
  if (!slug) return '';
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function normalizeMentor(m) {
  if (!m) return m;

  // If no _id at all → pure mock object, return as-is
  if (!m._id && typeof m.userId !== 'object') return m;

  // API data detected. userId may be a populated object OR already flattened.
  const hasPopulatedUser = typeof m.userId === 'object' && m.userId !== null;
  const user = hasPopulatedUser ? m.userId : {};

  // Name: API userId object may not carry firstName/lastName; derive from slug as fallback
  const firstName = m.firstName || user.firstName || '';
  const lastName  = m.lastName  || user.lastName  || '';
  const slugName  = nameFromSlug(m.slug);
  const fullName  = (m.name && m.name !== 'Mentor' && m.name.trim())
                  || `${firstName} ${lastName}`.trim()
                  || user.name
                  || slugName
                  || 'Mentor';

  // Derive first/last from slug when API doesn't return them
  const slugParts   = slugName.split(' ');
  const derivedFirst = firstName || slugParts[0] || '';
  const derivedLast  = lastName  || slugParts[1] || '';

  // Map session type id → display name
  const SESSION_ID_NAMES = {
    '1on1':           '1:1 Mentorship',
    'code_review':    'Code Review',
    'mock_interview': 'Mock Interview',
    'career_roadmap': 'Career Roadmap',
  };

  // Normalize sessionTypes — API returns objects, mock returns strings.
  // All pricing and metadata fields are forwarded explicitly so the booking
  // page can render them directly without falling back to local SESSION_TYPES.
  const sessionTypes = (m.sessionTypes || []).map(st => {
    if (typeof st === 'string') return { name: st, priceINR: m.priceINR || 0, duration: 60 };
    const displayName = st.name || SESSION_ID_NAMES[st.id] || st.id || '1:1 Session';
    return {
      ...st,
      name:             displayName,
      icon:             st.icon             || '🎯',
      description:      st.description      || '',
      duration:         st.duration         || st.durationMins || 60,
      // priceINR = actual charge; originalPriceINR = "was" price for strikethrough
      priceINR:         st.priceINR         || 0,
      originalPriceINR: st.originalPriceINR || 0,
      topics:           st.topics           || [],
      allowedPlatforms: st.allowedPlatforms || [],
      enabled:          st.enabled !== false,
    };
  }).filter(st => st.enabled !== false)
    .filter((st, idx, arr) => arr.findIndex(s => s.name === st.name) === idx); // dedupe by name

  return {
    ...m,
    id:         m._id || m.id,
    // Identity
    name:       fullName,
    firstName:  derivedFirst,
    lastName:   derivedLast,
    headline:   user.headline   || m.headline   || '',
    bio:        user.bio        || m.bio         || '',
    avatar:     user.avatar     || m.avatar      || '',
    avatarBg:   user.avatarBg   || m.avatarBg    || '4f46e5',
    location:   user.location   || m.location    || '',
    languages:  user.languages  || m.languages   || [],
    linkedinUrl:user.linkedinUrl || m.linkedinUrl || '',
    githubUrl:  user.githubUrl   || m.githubUrl   || '',
    initials:   derivedFirst && derivedLast
                  ? `${derivedFirst[0]}${derivedLast[0]}`
                  : (m.initials || fullName[0] || 'M'),
    // Role: API stores it in currentRole; fall back to parsing headline
    role:       m.currentRole || (() => {
      const h = user.headline || m.headline || '';
      return h.split('|')[0].trim() || m.expertise?.[0] || m.role || '';
    })(),
    // Company: API stores it directly on mentor doc; fall back to headline
    company:    m.company || (() => {
      const h = user.headline || m.headline || '';
      const parts = h.split('|');
      return (parts[1] || '').trim() || '';
    })(),
    experience: m.experience     || 0,
    skills:     m.skills         || [],
    rating:       m.avgRating     || m.rating       || 0,
    reviewCount:  m.reviewCount  || m.totalReviews || 0,
    totalReviews: m.reviewCount  || m.totalReviews || 0,
    sessionsCompleted: m.sessionsCompleted || 0,
    isAvailableToday:  m.isAvailableToday  || false,
    // Derived display fields
    avatarSeed:       user.avatar || m.avatarSeed || fullName,
    companyLogo:      m.companyLogo || '🏢',
    specialization:   m.specialization || m.expertise?.[0] || (m.skills || [])[0] || '',
    timezone:         m.timezone  || user.timezone  || 'Asia/Kolkata',
    responseTime:     m.responseTime || 'Within 24hrs',
    badge:            m.badge || (m.sessionsCompleted > 200 ? 'Top Mentor' : m.avgRating >= 4.9 ? 'Rising Star' : ''),
    badgeType:        m.badgeType || (m.sessionsCompleted > 200 ? 'gold' : 'blue'),
    availabilityLabel: m.availabilityLabel || (m.isAvailableToday ? '● Available Today' : '● This Week'),
    availabilityColor: m.availabilityColor || (m.isAvailableToday ? '#16a34a' : '#b45309'),
    // Scheduling rules — used by BookingPage to filter dates/slots
    sameDayBooking:        m.sameDayBooking        ?? true,
    minHoursBeforeBooking: m.minHoursBeforeBooking ?? 3,
    minDaysBeforeBooking:  m.minDaysBeforeBooking  ?? 1,
    maxDaysAhead:          m.maxDaysAhead          ?? 30,
    // Pricing
    priceINR:    sessionTypes[0]?.priceINR || m.priceINR || 0,
    sessionTypes,
  };
}

const mentorService = {

  // ── Get all mentors (with filters) ────────────────────────
  async getMentors({ page = 1, limit = 12, skills, minPrice, maxPrice, rating, availability, sort, search } = {}) {
    if (API_CONFIG.MOCK) {
      await delay();
      let filtered = [...MENTORS];

      if (search)       filtered = filtered.filter(m => m.name.toLowerCase().includes(search.toLowerCase()) || m.skills.some(s => s.toLowerCase().includes(search.toLowerCase())));
      if (skills)       filtered = filtered.filter(m => skills.split(',').some(s => m.skills.includes(s.trim())));
      if (minPrice)     filtered = filtered.filter(m => m.priceINR >= parseInt(minPrice));
      if (maxPrice)     filtered = filtered.filter(m => m.priceINR <= parseInt(maxPrice));
      if (rating)       filtered = filtered.filter(m => m.rating >= parseFloat(rating));
      if (availability === 'today') filtered = filtered.filter(m => m.isAvailableToday);

      if (sort === 'price_asc')  filtered.sort((a, b) => a.priceINR - b.priceINR);
      if (sort === 'price_desc') filtered.sort((a, b) => b.priceINR - a.priceINR);
      if (sort === 'rating')     filtered.sort((a, b) => b.rating  - a.rating);

      const start = (page - 1) * limit;
      return {
        mentors:    filtered.slice(start, start + limit),
        total:      filtered.length,
        page,
        totalPages: Math.ceil(filtered.length / limit),
      };
    }
    const params = { page, limit, ...(skills && { skills }), ...(minPrice && { minPrice }), ...(maxPrice && { maxPrice }), ...(rating && { rating }), ...(availability && { availability }), ...(sort && { sort }), ...(search && { search }) };
    const cacheKey = `mentors:${JSON.stringify(params)}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
    return apiClient.get('/mentors', { params }).then(res => {
      const result = { ...res, mentors: (res.mentors || []).map(normalizeMentor) };
      setCached(cacheKey, result);
      return result;
    });
  },

  // ── Get mentor by slug ─────────────────────────────────────
  async getMentorBySlug(slug) {
    if (API_CONFIG.MOCK) {
      await delay();
      const mentor = MENTORS.find(m => m.slug === slug);
      if (!mentor) { const e = new Error('Mentor not found.'); e.code = 'NOT_FOUND'; throw e; }
      return { mentor };
    }
    return apiClient.get(`/mentors/${slug}`).then(res => ({
      ...res,
      mentor: normalizeMentor(res.mentor || res),
    }));
  },

  // ── Get availability slots ─────────────────────────────────
  async getAvailability(mentorId, date) {
    if (API_CONFIG.MOCK) {
      await delay();
      const slots = ['09:00','10:00','11:00','14:00','15:00','16:00','17:00'].map(time => ({
        time, available: Math.random() > 0.3,
      }));
      return { date, slots };
    }
    return apiClient.get(`/mentors/${mentorId}/availability`, { params: { date } });
  },

  // ── Update mentor profile ──────────────────────────────────
  async updateProfile(data) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { mentor: { ...data }, message: '[MOCK] Profile updated.' };
    }
    return apiClient.put('/mentors/me/profile', data);
  },

  // ── Update availability ────────────────────────────────────
  async updateAvailability(slots) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { mentor: { availabilitySlots: slots }, message: '[MOCK] Availability updated.' };
    }
    return apiClient.put('/mentors/me/availability', { slots });
  },
};

export default mentorService;
